import { createElement, memo, useMemo } from 'react'
import { useUser, useUserTokens } from '@/queries/hooks/use-user'
import environment from '_environments'
import {
  RELEASED_CHANGELOG_TRIGGER_ID,
  RELEASED_DEFAULT_CHANNEL_ID,
} from './released-constants'

/**
 * Mounts Released's floating widget (not inline). Uses `trigger` to open from the sidebar bell.
 *
 * Rendered at the App level so it persists across route changes (never unmounts
 * during a login session). Wrapped in `memo` to avoid re-renders from parent
 * state changes — only its own hooks (`useUser`, `useUserTokens`) can trigger
 * a re-render, and the memoized props object ensures the custom element's DOM
 * attributes are only touched when `channelId` or `authToken` actually change.
 *
 * @see https://docs.released.so/guide/getting-started/setup-guide/embedding-the-widget
 */
export const ReleasedWidget = memo(function ReleasedWidget() {
  const { data: user } = useUser()
  const { data: tokens } = useUserTokens(user?.UUID)

  const channelId = useMemo(() => {
    const fromEnv = environment.keys.released?.channelId?.trim()
    return fromEnv && fromEnv.length > 0 ? fromEnv : RELEASED_DEFAULT_CHANNEL_ID
  }, [])

  const authToken = tokens?.releasedToken ?? null

  const props = useMemo(() => {
    const base: Record<string, string> = {
      'channel-id': channelId,
      trigger: `#${RELEASED_CHANGELOG_TRIGGER_ID}`,
      badge: 'true',
      refresh: '5m',
      anchor: 'viewport',
      position: 'bottom-left',
      'z-index': '10050',
      modules: 'all',
    }
    if (authToken) {
      base['auth-token'] = authToken
    }
    return base
  }, [channelId, authToken])

  return createElement('released-widget', props)
})
